// Mock API Service - Returns mock data for frontend-only deployment
import { API_CONFIG } from '../config/api';

// Helper to simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Generate mock time series data
const generateTimeSeriesData = (startDate, endDate, intervalMinutes = 15) => {
  const data = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  let current = new Date(start);
  
  while (current <= end) {
    const hour = current.getHours();
    const baseValue = 50 + Math.sin((hour - 6) * Math.PI / 12) * 40; // Simulate solar curve
    const randomVariation = (Math.random() - 0.5) * 10;
    
    data.push({
      timestamp: current.toISOString(),
      datetime: current.toISOString(),
      power: Math.max(0, baseValue + randomVariation),
      forecast: Math.max(0, baseValue + randomVariation + (Math.random() - 0.5) * 5),
      irradiance: Math.max(0, baseValue * 0.8 + randomVariation),
      temperature: 25 + (Math.random() - 0.5) * 5,
      wind_speed: 3 + Math.random() * 5,
      ambient_temp: 28 + (Math.random() - 0.5) * 3
    });
    
    current = new Date(current.getTime() + intervalMinutes * 60000);
  }
  
  return data;
};

// Mock data generators
const mockData = {
  // Authentication
  login: async (credentials) => {
    await delay(800);
    return {
      data: {
        access_token: 'mock_access_token_' + Date.now(),
        refresh_token: 'mock_refresh_token_' + Date.now(),
        token_type: 'bearer',
        expires_in: 3600,
        user: {
          id: '1',
          username: credentials.username,
          email: credentials.username,
          role: 'admin',
          full_name: 'Demo User',
          is_active: true
        }
      }
    };
  },

  refreshToken: async () => {
    await delay(300);
    return {
      data: {
        access_token: 'mock_access_token_refreshed_' + Date.now(),
        refresh_token: 'mock_refresh_token_' + Date.now(),
        token_type: 'bearer',
        expires_in: 3600
      }
    };
  },

  getUserData: async () => {
    await delay(300);
    return {
      data: {
        id: '1',
        username: 'admin@rexonpower.com',
        email: 'admin@rexonpower.com',
        role: 'admin',
        full_name: 'Demo User',
        is_active: true
      }
    };
  },

  // Energy Loss / EYA Data
  getAggregatedEya: async (params) => {
    await delay(600);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Generate inverter and weather sensor IDs for asset management
    const inverterIds = ['INV-001', 'INV-002', 'INV-003', 'INV-004', 'INV-005'];
    const wmsIds = ['WS-001', 'WS-002', 'WS-003'];
    
    const inverterClippingMap = {};
    const soilingWmsMap = {};
    
    inverterIds.forEach(id => {
      inverterClippingMap[id] = -0.5 + (Math.random() - 0.5) * 0.3;
    });
    
    wmsIds.forEach(id => {
      soilingWmsMap[id] = -2.5 + (Math.random() - 0.5) * 0.5;
    });
    
    return {
      data: [{
        asset_id: params.plant_id || API_CONFIG.PLANT_ID,
        readings: [{
          EYA: {
            actual_energy_export: 85.5,
            actual_energy_export_KWh: 85500,
            target_energy_export: 100000,
            daily_poa_energy: 5.8,
            irradiation_loss: 95.2,
            soiling_loss: -2.5,
            shading_loss: -1.8,
            pvmodule_temperature_loss: -3.2,
            dc_cable_loss: -0.8,
            inverter_efficiency_loss: -2.1,
            plant_clipping_loss_percentage: -0.5,
            plant_deration_loss_percentage: -0.3,
            trafo_effi_ac_cable_loss: -1.2,
            plant_availability: {
              plant_unavailability: -0.8
            },
            grid_availability: {
              grid_unavailability: -0.2
            },
            auxillary_loss: -0.5,
            miscloss: -1.5,
            tracker_loss: -0.3,
            // Asset Management data
            inverter_clipping_loss_list_percentage: inverterClippingMap,
            soiling_loss_wms_level: soilingWmsMap,
            // Inverter Efficiency data
            inverter_efficiency_loss_percentage: -2.1,
            inverter_efficiency_loss_inverter_wise: [{
              'INV-001': -2.0,
              'INV-002': -2.1,
              'INV-003': -2.2,
              'INV-004': -1.9,
              'INV-005': -2.3
            }],
            inverter_breakdown_loss_list_kwh: {
              'INV-001': 10.5,
              'INV-002': 12.3,
              'INV-003': 8.7,
              'INV-004': 15.2,
              'INV-005': 9.8
            },
            inverter_breakdown_kwh_inverter_wise: {
              'INV-001': 10.5,
              'INV-002': 12.3,
              'INV-003': 8.7,
              'INV-004': 15.2,
              'INV-005': 9.8
            },
            plant_breakdown_loss_kwh: 56.5,
            inverter_breakdown_kwh: 56.5,
            inverter_clipping_percentage_inverter_wise: inverterClippingMap,
            // Daily summary data
            generation_start_time: '05:10',
            generation_end_time: '18:25',
            total_generation_time: '13:15',
            total_hrs_gpoa_gt_100: '12:00',
            total_hrs_gpoa_gt_500: '10:10',
            peak_maximum_power: 151.01,
            peak_maximum_poa: 1018.38,
            poi1_exported_energy: 1509.4,
            poi2_exported_energy: 1487.4,
            total_exported_energy: 2996.8,
            total_imported_energy: 12.6,
            inverter_availability: 100,
            inverter_breakdown_loss: 0.01,
            module_temperature_loss: 4.52,
            total_energy_loss: 20.94
          }
        }]
      }]
    };
  },

  // PVSyst Data
  getPvSystData: async (params) => {
    await delay(500);
    const currentYear = new Date().getFullYear();
    const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const yearMonth = `${currentYear}-${currentMonth}`;
    
    return {
      data: {
        json_object: {
          PVSyst: [
            {
              code: 'PLANT_PERFORMANCE_RATIO_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 82.5
              }
            },
            {
              code: 'PLANT_POA_IRRADIATION_ENERGY_PVSYST',
              unit: 'kWh/m²',
              valueMatrix: {
                [yearMonth]: 5.8
              }
            },
            {
              code: 'PLANT_UNAVAILABILITY_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.8
              }
            },
            {
              code: 'PLANT_GRID_UNAVAILABILITY_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.2
              }
            },
            {
              code: 'PLANT_DC_CABLE_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.8
              }
            },
            {
              code: 'PLANT_INVERTER_EFFICIENCY_LOSS_TARGET',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 2.1
              }
            },
            {
              code: 'PLANT_TRAFO_AND_AC_CABLE_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 1.2
              }
            },
            {
              code: 'PLANT_SOILING_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 2.5
              }
            },
            {
              code: 'PLANT_MODULE_TEMPERATURE_LOSS_PERCENTAGE',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 3.2
              }
            },
            {
              code: 'PLANT_SHADING_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 1.8
              }
            },
            {
              code: 'PLANT_CLIPPING_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.5
              }
            },
            {
              code: 'PLANT_AUX_LOSS_TARGET',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.5
              }
            },
            {
              code: 'PLANT_DERATION_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.3
              }
            },
            {
              code: 'PLANT_MODULE_DEGRADATION_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.5
              }
            },
            {
              code: 'PLANT_LID_LIGHT_INDUCED_DEGRADATION_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.3
              }
            },
            {
              code: 'PLANT_MODULE_ARRAY_MISMATCH_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.4
              }
            },
            {
              code: 'PLANT_IAM_LOSS_PVSYST',
              unit: '%',
              valueMatrix: {
                [yearMonth]: 0.2
              }
            }
          ]
        }
      }
    };
  },

  // Graph Data / KPI Readings
  getForGraph: async (params) => {
    await delay(500);
    
    // For historical data, generate multiple readings over time
    const startDate = new Date(params.start_date);
    const endDate = new Date(params.end_date);
    const isHistorical = startDate.toDateString() !== endDate.toDateString();
    
    if (isHistorical) {
      // Generate daily readings for historical data
      const readings = [];
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dayOffset = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
        const variation = (Math.sin(dayOffset * 0.3) * 2) + (Math.random() - 0.5) * 1;
        
        readings.push({
          asset_id: params.plant_id || API_CONFIG.PLANT_ID,
          timestamp: currentDate.toISOString(),
          readings: [{
            KPI: [
              { performance_ratio: 85.2 + variation },
              { plant_availability: 98.5 + variation * 0.5 },
              { energy_export: 85500 + variation * 1000 },
              { energy_import: 120 + variation * 10 },
              { energy_yield: 85500 + variation * 1000 },
              { specific_yield: 4.2 + variation * 0.1 },
              { capacity_factor: 35.6 + variation * 0.5 },
              { irradiation: 5.8 + variation * 0.1 },
              { expected_energy: 100000 + variation * 2000 },
              { net_energy: 85380 + variation * 1000 },
              { specific_power: 0.85 + variation * 0.01 },
              { temp_corr_ratio: 87.5 + variation },
              { grid_availability: 99.8 + variation * 0.1 },
              { dc_cuf_daily: 35.6 + variation * 0.5 },
              { ac_cuf_daily: 35.2 + variation * 0.5 }
            ]
          }]
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return {
        data: readings
      };
    }
    
    // Single reading for current day
    return {
      data: [{
        asset_id: params.plant_id || API_CONFIG.PLANT_ID,
        readings: [{
          KPI: [
            { performance_ratio: 85.2 },
            { plant_availability: 98.5 },
            { energy_export: 85500 },
            { energy_import: 120 },
            { energy_yield: 85500 },
            { specific_yield: 4.2 },
            { capacity_factor: 35.6 },
            { irradiation: 5.8 },
            { expected_energy: 100000 },
            { net_energy: 85380 },
            { specific_power: 0.85 },
            { temp_corr_ratio: 87.5 },
            { grid_availability: 99.8 },
            { dc_cuf_daily: 35.6 },
            { ac_cuf_daily: 35.2 }
          ]
        }]
      }]
    };
  },

  // Aggregated KPI Data (for Historical Tech Par)
  getAggregatedKpi: async (params) => {
    await delay(500);
    
    const startDate = new Date(params.start_date);
    const endDate = new Date(params.end_date);
    
    // Generate daily readings
    const readings = [];
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dayOffset = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
      const variation = (Math.sin(dayOffset * 0.3) * 2) + (Math.random() - 0.5) * 1;
      
      readings.push({
        asset_id: params.plant_id || API_CONFIG.PLANT_ID,
        timestamp: currentDate.toISOString(),
        readings: [{
          KPI: {
            performance_ratio: 85.2 + variation,
            plant_availability: 98.5 + variation * 0.5,
            grid_availability: 99.8 + variation * 0.1,
            energy_export: 85500 + variation * 1000,
            energy_import: 120 + variation * 10,
            net_energy: 85380 + variation * 1000,
            specific_yield: 4.2 + variation * 0.1,
            irradiation: 5.8 + variation * 0.1,
            expected_energy: 100000 + variation * 2000,
            temp_corr_ratio: 87.5 + variation,
            dc_cuf_daily: 35.6 + variation * 0.5,
            ac_cuf_daily: 35.2 + variation * 0.5
          }
        }]
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      data: readings
    };
  },

  // KPI Readings by Tags
  getKpiReadingsByTags: async (params) => {
    await delay(500);
    
    // Generate mock KPI values based on common tags
    const baseKpiValues = {
      performance_ratio: 85.2,
      plant_availability: 98.5,
      grid_availability: 99.8,
      net_energy: 85380,
      specific_yield: 4.2,
      specific_power: 0.85,
      energy_export: 85500,
      energy_import: 120,
      irradiation: 5.8,
      expected_energy: 100000,
      temp_corr_ratio: 87.5,
      dc_cuf_daily: 35.6,
      ac_cuf_daily: 35.2,
      capacity_factor: 35.6,
      pr_daily: 85.2,
      pr_monthly: 84.8,
      pr_yearly: 83.5,
      poa_irradiance: 5.8,
      ambient_temperature: 28.5,
      module_temperature: 45.2,
      wind_speed: 4.2,
      humidity: 65.0
    };
    
    // For heatmap, we need multiple readings over time (daily data)
    const startDate = new Date(params.start_date);
    const endDate = new Date(params.end_date);
    const readings = [];
    let currentDate = new Date(startDate);
    
    // Generate daily readings
    while (currentDate <= endDate) {
      const timestamp = currentDate.toISOString();
      const dayOffset = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
      
      // Add some variation to make it realistic
      const variation = (Math.sin(dayOffset * 0.5) * 2) + (Math.random() - 0.5) * 1;
      
      const kpiArray = params.tags_list?.map(tag => {
        const baseValue = baseKpiValues[tag] || 85;
        let value = baseValue + variation;
        
        // Ensure values stay in reasonable ranges
        if (tag.includes('ratio') || tag.includes('availability') || tag.includes('cuf')) {
          value = Math.max(70, Math.min(100, value));
        } else if (tag === 'specific_yield') {
          value = Math.max(3.0, Math.min(5.5, value));
        } else if (tag === 'specific_power') {
          value = Math.max(0.7, Math.min(0.95, value));
        } else if (tag.includes('energy')) {
          value = Math.max(0, value);
        }
        
        return { [tag]: Number(value.toFixed(2)) };
      }) || [];
      
      readings.push({
        asset_id: params.plant_id || API_CONFIG.PLANT_ID,
        timestamp: timestamp,
        readings: [{
          KPI: kpiArray
        }]
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // If only one reading is needed (for real-time), return single reading
    if (readings.length === 0 || startDate.toDateString() === endDate.toDateString()) {
      const kpiArray = params.tags_list?.map(tag => {
        const value = baseKpiValues[tag] || (Math.random() * 100);
        return { [tag]: value };
      }) || [];
      
      return {
        data: [{
          asset_id: params.plant_id || API_CONFIG.PLANT_ID,
          timestamp: new Date().toISOString(),
          readings: [{
            KPI: kpiArray
          }]
        }]
      };
    }
    
    return {
      data: readings
    };
  },

  // Power Forecast - Intra Day
  getIntraDayForecast: async (params) => {
    await delay(500);
    const date = params.date || new Date().toISOString().split('T')[0];
    const startDate = `${date}T00:00:00`;
    const endDate = `${date}T23:59:59`;
    const timeSeries = generateTimeSeriesData(startDate, endDate, 15);
    
    // Build json_object structure for Power Forecast
    const activePowerJson = {};
    const irradianceJson = {};
    
    timeSeries.forEach(d => {
      const timestamp = d.timestamp;
      activePowerJson[timestamp] = {
        active_power_actual: d.power,
        active_power_forecasted: d.forecast,
        actual: d.power,
        forecasted: d.forecast,
        deviation: d.forecast - d.power
      };
      
      irradianceJson[timestamp] = {
        Irradiance_actual: d.irradiance,
        Irradiance_forecasted: d.irradiance * 1.05,
        actual: d.irradiance,
        forecasted: d.irradiance * 1.05,
        deviation: (d.irradiance * 1.05) - d.irradiance,
        wind_speed: d.wind_speed,
        ambient_temp: d.ambient_temp
      };
    });
    
    return {
      data: {
        json_object: {
          active_power_json: activePowerJson,
          irradiance_json: irradianceJson,
          cumulative: {
            energy_export_actual: 85500,
            energy_export_forecasted: 87000,
            energy_deviation: 1.75,
            energy_actual: 85500,
            energy_forecasted: 87000,
            RMSE: 2.5,
            MAE: 1.8,
            MAPE: 2.1,
            irradiation_actual: 5.8,
            irradiation_forecasted: 6.0,
            irradiation_deviation: 3.45,
            wind_speed: 4.2,
            ambient_temp: 28.5
          }
        },
        power: timeSeries.map(d => ({
          timestamp: d.timestamp,
          actual: d.power,
          forecast: d.forecast
        })),
        irradiance: timeSeries.map(d => ({
          timestamp: d.timestamp,
          actual: d.irradiance,
          forecast: d.irradiance * 1.05
        })),
        weather: timeSeries.map(d => ({
          timestamp: d.timestamp,
          wind_speed: d.wind_speed,
          ambient_temp: d.ambient_temp
        })),
        cumulative: {
          power_cumulative_actual: 85500,
          power_cumulative_forecasted: 87000,
          power_cumulative_deviation: 1.75,
          power_RMSE: 2.5,
          power_MAE: 1.8,
          power_MAPE: 2.1,
          irradiance_cumulative_actual: 5.8,
          irradiance_cumulative_forecasted: 6.0,
          irradiance_cumulative_deviation: 3.45,
          wind_speed_avg: 4.2,
          ambient_temp_avg: 28.5
        }
      }
    };
  },

  // Power Forecast - Inter Day
  getInterDayForecast: async (params) => {
    await delay(500);
    const date = params.date || new Date().toISOString().split('T')[0];
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    
    const activePowerJson = {};
    const irradianceJson = {};
    
    dates.forEach(d => {
      const actual = 85000 + (Math.random() - 0.5) * 10000;
      const forecast = 87000 + (Math.random() - 0.5) * 10000;
      const timestamp = `${d} 12:00:00`;
      
      activePowerJson[timestamp] = {
        active_power_actual: actual,
        active_power_forecasted: forecast,
        power_forecasted: forecast,
        actual: actual,
        forecasted: forecast
      };
      
      irradianceJson[timestamp] = {
        Irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        GHI_Irradiance_Forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        ghi_irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        wind_speed: 4.2 + (Math.random() - 0.5) * 1
      };
    });
    
    return {
      data: {
        json_object: {
          active_power_json: activePowerJson,
          irradiance_json: irradianceJson
        },
        power: dates.map(d => ({
          date: d,
          actual: 85000 + (Math.random() - 0.5) * 10000,
          forecast: 87000 + (Math.random() - 0.5) * 10000
        })),
        cumulative: {
          power_cumulative_actual: 595000,
          power_cumulative_forecasted: 609000,
          power_cumulative_deviation: 2.35
        }
      }
    };
  },

  // Power Forecast - Week Ahead
  getWeekAheadForecast: async (params) => {
    await delay(500);
    const date = params.date || new Date().toISOString().split('T')[0];
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    
    const activePowerJson = {};
    const irradianceJson = {};
    
    dates.forEach(d => {
      const forecast = 85000 + (Math.random() - 0.5) * 10000;
      const timestamp = `${d} 12:00:00`;
      
      activePowerJson[timestamp] = {
        active_power_forecasted: forecast,
        power_forecasted: forecast
      };
      
      irradianceJson[timestamp] = {
        Irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        GHI_Irradiance_Forecasted: 5.8 + (Math.random() - 0.5) * 0.5,
        ghi_irradiance_forecasted: 5.8 + (Math.random() - 0.5) * 0.5
      };
    });
    
    return {
      data: {
        json_object: {
          active_power_json: activePowerJson,
          irradiance_json: irradianceJson
        },
        power: dates.map(d => ({
          date: d,
          forecast: 85000 + (Math.random() - 0.5) * 10000
        }))
      }
    };
  },

  // Power Forecast - Month Ahead
  getMonthAheadForecast: async (params) => {
    await delay(500);
    const daysInMonth = 30;
    const dates = [];
    const activePowerJson = {};
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${params.month || '2024-08'}-${i.toString().padStart(2, '0')}`;
      const forecast = 85000 + (Math.random() - 0.5) * 10000;
      dates.push({
        date: dateStr,
        forecast: forecast
      });
      
      const timestamp = `${dateStr} 12:00:00`;
      activePowerJson[timestamp] = {
        active_power_forecasted: forecast,
        power_forecasted: forecast,
        energy_export_forecasted: forecast / 1000 // Convert to MWh
      };
    }
    
    return {
      data: {
        json_object: {
          active_power_json: activePowerJson
        },
        power: dates
      }
    };
  },

  // Power Forecast - Historical
  getHistoricalForecast: async (params) => {
    await delay(500);
    const start = new Date(params.start_date);
    const end = new Date(params.end_date);
    const dates = [];
    const activePowerJson = {};
    const irradianceJson = {};
    let current = new Date(start);
    
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const actual = 85000 + (Math.random() - 0.5) * 10000;
      const forecast = 87000 + (Math.random() - 0.5) * 10000;
      
      dates.push({
        date: dateStr,
        actual: actual,
        forecast: forecast
      });
      
      // Generate hourly data for the day
      for (let hour = 6; hour < 19; hour++) {
        const timestamp = `${dateStr} ${hour.toString().padStart(2, '0')}:00:00`;
        const hourActual = actual * (0.3 + Math.sin((hour - 6) * Math.PI / 13) * 0.7);
        const hourForecast = forecast * (0.3 + Math.sin((hour - 6) * Math.PI / 13) * 0.7);
        
        activePowerJson[timestamp] = {
          active_power_actual: hourActual,
          active_power_forecasted: hourForecast,
          actual: hourActual,
          forecasted: hourForecast,
          deviation: hourForecast - hourActual
        };
        
        irradianceJson[timestamp] = {
          Irradiance_actual: hourActual * 0.01,
          Irradiance_forecasted: hourForecast * 0.01,
          actual: hourActual * 0.01,
          forecasted: hourForecast * 0.01,
          deviation: (hourForecast - hourActual) * 0.01
        };
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return {
      data: {
        json_object: {
          active_power_json: activePowerJson,
          irradiance_json: irradianceJson
        },
        power: dates,
        cumulative: {
          power_cumulative_actual: dates.reduce((sum, d) => sum + d.actual, 0),
          power_cumulative_forecasted: dates.reduce((sum, d) => sum + d.forecast, 0),
          power_cumulative_deviation: 2.1
        }
      }
    };
  },

  // Asset Data
  fetchApoAssetData: async (params) => {
    await delay(500);
    return {
      data: {
        assets: [
          {
            id: 'inv-001',
            name: 'Inverter 1',
            type: 'INVERTER',
            make: 'SMA',
            model: 'SUNNY TRIPOWER 15000',
            ac_rated_capacity: 15000,
            status: 'active',
            location: 'String 1'
          },
          {
            id: 'inv-002',
            name: 'Inverter 2',
            type: 'INVERTER',
            make: 'SMA',
            model: 'SUNNY TRIPOWER 15000',
            ac_rated_capacity: 15000,
            status: 'active',
            location: 'String 2'
          }
        ]
      }
    };
  },

  fetchApoAssetDataForAssetTable: async (params) => {
    await delay(500);
    return {
      data: {
        assets: Array.from({ length: 10 }, (_, i) => ({
          id: `inv-${(i + 1).toString().padStart(3, '0')}`,
          name: `Inverter ${i + 1}`,
          make: 'SMA',
          model: 'SUNNY TRIPOWER 15000',
          ac_rated_capacity: 15000,
          jhi_type: 'INVERTER',
          status: i % 10 === 0 ? 'maintenance' : 'active'
        }))
      }
    };
  },

  fetchApoAssetAttrData: async (params) => {
    await delay(500);
    return {
      data: {
        attributes: {
          power: 14500,
          voltage: 400,
          current: 36.25,
          frequency: 50,
          temperature: 45.2,
          efficiency: 96.5
        }
      }
    };
  },

  // OMP Data by Tags
  getOmpDataByTags: async (params) => {
    await delay(500);
    const startDate = params.start_date || new Date().toISOString();
    const endDate = params.end_date || new Date().toISOString();
    const timeSeries = generateTimeSeriesData(startDate, endDate, 15);
    
    // Build json_object structure for live trend data
    const jsonObject = timeSeries.map(d => {
      const reading = {
        ts: d.timestamp, // PerformanceTimeSeriesChart looks for 'ts'
        timestamp: d.timestamp,
        DP_ACTIVE_POWER: d.power,
        WS_LIVE_POA_POWER_DENSITY: d.irradiance,
        DP_PANEL_DP_ACTIVE_POWER: d.power,
        DP_PANEL_DP_APPARENT_POWER: d.power * 1.1,
        DP_PANEL_DP_REACTIVE_POWER_2: d.power * 0.2,
        DP_PANEL_DP_AVERAGE_PHASE_RYB_FREQUENCY_2: 50.0 + (Math.random() - 0.5) * 0.1,
        DP_PANEL_DP_RMS_PHASE_RYB_VOLTAGE: 400000 + (Math.random() - 0.5) * 10000,
        DP_PANEL_DP_SPECIFIC_POWER: d.power / 250, // Assuming 250 MWp capacity
        'WMS-02_WS_LIVE_POA_POWER_DENSITY': d.irradiance, // PerformanceTimeSeriesChart looks for this
        WS_LIVE_GHI_POWER_DENSITY: d.irradiance * 0.9,
        WS_AMBIENT_TEMPERATURE: d.ambient_temp,
        WS_MODULE_TEMPERATURE_01: d.temperature,
        WS_WIND_SPEED: d.wind_speed,
        WS_WIND_DIRECTION: Math.floor(Math.random() * 360)
      };
      return reading;
    });
    
    return {
      data: {
        json_object: jsonObject
      }
    };
  },

  // Data Acquisition Status
  fetchDataAcquisitionStatus: async (params) => {
    await delay(300);
    return {
      data: {
        status: 'active',
        last_update: new Date().toISOString(),
        halted: false
      }
    };
  }
};

// Mock API Service Class
class MockApiService {
  constructor() {
    this.cache = new Map();
  }

  buildKey(url, body) {
    return `${url}|${body ? JSON.stringify(body) : ''}`;
  }

  async postCached(url, body, ttlMs) {
    const key = this.buildKey(url, body);
    const now = Date.now();
    const cached = this.cache.get(key);
    
    if (cached && cached.expiresAt > now) {
      return cached.response;
    }
    
    const response = await this.post(url, body);
    this.cache.set(key, {
      expiresAt: now + ttlMs,
      response: response
    });
    return response;
  }

  async post(url, body) {
    // Map endpoints to mock functions
    if (url.includes('/auth/login') || url === API_CONFIG.ENDPOINTS.LOGIN) {
      return await mockData.login(body);
    }
    
    if (url.includes('/token') && body?.grant_type === 'refresh_token') {
      return await mockData.refreshToken();
    }
    
    if (url.includes('get_aggregated_eya')) {
      return await mockData.getAggregatedEya(body);
    }
    
    if (url.includes('fetch_pvsyst_data')) {
      return await mockData.getPvSystData(body);
    }
    
    if (url.includes('get_for_graph')) {
      return await mockData.getForGraph(body);
    }
    
    if (url.includes('kpi_readings_by_tags')) {
      return await mockData.getKpiReadingsByTags(body);
    }
    
    if (url.includes('get_intra')) {
      return await mockData.getIntraDayForecast(body);
    }
    
    if (url.includes('get_inter')) {
      return await mockData.getInterDayForecast(body);
    }
    
    if (url.includes('get_weekahead')) {
      return await mockData.getWeekAheadForecast(body);
    }
    
    if (url.includes('get_monthahead')) {
      return await mockData.getMonthAheadForecast(body);
    }
    
    if (url.includes('get_historic')) {
      return await mockData.getHistoricalForecast(body);
    }
    
    if (url.includes('fetch_apo_asset_data_for_asset_table')) {
      return await mockData.fetchApoAssetDataForAssetTable(body);
    }
    
    if (url.includes('fetch_apo_asset_data')) {
      return await mockData.fetchApoAssetData(body);
    }
    
    if (url.includes('fetch_apo_asset_attr_data')) {
      return await mockData.fetchApoAssetAttrData(body);
    }
    
    if (url.includes('fetch_omp_data_by_tags')) {
      return await mockData.getOmpDataByTags(body);
    }
    
    if (url.includes('get_for_energy_loss')) {
      return await mockData.getAggregatedEya(body);
    }
    
    if (url.includes('get_aggregated_kpi')) {
      return await mockData.getAggregatedKpi(body);
    }
    
    if (url.includes('inverter-availability') || url.includes('get_inverter_availability')) {
      return await this.getInverterAvailability(body);
    }
    
    if (url.includes('string-combiner-box-availability') || url.includes('get_string_combiner_box_availability')) {
      return await this.getStringCombinerBoxAvailability(body);
    }
    
    if (url.includes('tracker-availability') || url.includes('get_tracker_availability')) {
      return await this.getTrackerAvailability(body);
    }
    
    // Default fallback
    console.warn('Mock API: Unknown endpoint', url);
    return { data: {} };
  }

  async get(url, params) {
    if (url.includes('/user/profile') || url.includes('USER_PROFILE')) {
      return await mockData.getUserData();
    }
    
    if (url.includes('fetch_data_acquisition_status')) {
      return await mockData.fetchDataAcquisitionStatus(params);
    }
    
    // Default fallback
    console.warn('Mock API: Unknown GET endpoint', url);
    return { data: {} };
  }

  async login(credentials) {
    return await this.post(API_CONFIG.ENDPOINTS.LOGIN, credentials);
  }

  async refreshToken(refreshToken) {
    return await this.post(API_CONFIG.ENDPOINTS.REFRESH_TOKEN, {
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    });
  }

  async logout() {
    // No-op for mock
  }

  async getUserData() {
    return await this.get(API_CONFIG.ENDPOINTS.USER_PROFILE);
  }

  async getForEnergyLoss(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_FOR_ENERGY_LOSS,
      params,
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getForGraph(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_FOR_GRAPH,
      params,
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getAggregatedKpi(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_AGGREGATED_KPI, 
      params, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getAggregatedEya(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_AGGREGATED_EYA,
      params,
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getKpiReadingsByTags(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.KPI_READINGS_BY_TAGS,
      params,
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getPvSystData(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_PVSYST_DATA,
      params,
      API_CONFIG.CACHE_TTL.LONG
    );
  }

  async getPvSystDataForEL(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_PVSYST_DATA_FOR_EL,
      params,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async getOmpDataByTags(params) {
    const requestBody = {
      ...params,
      tags_list: params.tags_list || ['DP_ACTIVE_POWER', 'WS_LIVE_POA_POWER_DENSITY']
    };
    
    return await this.postCached(
      API_CONFIG.ENDPOINTS.OMP_DATA_BY_TAGS,
      requestBody,
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  async getIntraDayForecast(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.INTRA_DAY_FORECAST,
      params,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async getInterDayForecast(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.INTER_DAY_FORECAST,
      params,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async getWeekAheadForecast(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.WEEK_AHEAD_FORECAST,
      params,
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  async getMonthAheadForecast(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.MONTH_AHEAD_FORECAST,
      params,
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  async getHistoricalForecast(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.HISTORICAL_FORECAST,
      params,
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  async fetchApoAssetData(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_DATA,
      params,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async fetchApoAssetDataForAssetTable(params) {
    const requestParams = {
      ...params,
      plant_id: params?.plant_id || API_CONFIG.PLANT_ID,
      asset_type: params?.asset_type || 'INVERTER',
      tag_list: params?.tag_list || ['make', 'model', 'ac_rated_capacity', 'jhi_type']
    };
    
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_DATA_FOR_TABLE,
      requestParams,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async fetchApoAssetAttrData(params) {
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_ATTR_DATA,
      params,
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async getUsers(params = {}) {
    return { data: [] };
  }

  async getUserPlantAccess(params = {}) {
    return { data: [] };
  }

  async updateUser(userId, userData) {
    return { data: { ...userData, id: userId } };
  }

  // Plant Availability endpoints
  async getInverterAvailability(params) {
    await delay(500);
    const heatmap = Array.from({ length: 90 }, (_, i) => {
      const rand = Math.random();
      if (rand < 0.1) return 0;
      if (rand < 0.3) return 83 + Math.random() * 1;
      if (rand < 0.5) return 84 + Math.random() * 2;
      if (rand < 0.8) return 86 + Math.random() * 2;
      return 88 + Math.random() * 2;
    });
    
    const values = heatmap.filter(v => v > 0);
    return {
      data: {
        heatmap,
        average: values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 79.2,
        best: Math.max(...heatmap),
        worst: Math.min(...heatmap)
      }
    };
  }

  async getStringCombinerBoxAvailability(params) {
    await delay(500);
    const generateInverterData = () => {
      return Array.from({ length: 42 }, (_, i) => {
        const rand = Math.random();
        if (rand < 0.1) return 0;
        if (rand < 0.3) return 83 + Math.random() * 1;
        if (rand < 0.5) return 84 + Math.random() * 2;
        if (rand < 0.8) return 86 + Math.random() * 2;
        return 88 + Math.random() * 2;
      });
    };
    
    const heatmaps = {
      inverter_1: generateInverterData(),
      inverter_2: generateInverterData()
    };
    
    const allValues = [...heatmaps.inverter_1, ...heatmaps.inverter_2].filter(v => v > 0);
    return {
      data: {
        heatmaps,
        average: allValues.length > 0 ? allValues.reduce((a, b) => a + b, 0) / allValues.length : 79.2,
        best: Math.max(...[...heatmaps.inverter_1, ...heatmaps.inverter_2]),
        worst: Math.min(...[...heatmaps.inverter_1, ...heatmaps.inverter_2])
      }
    };
  }

  async getTrackerAvailability(params) {
    await delay(500);
    const heatmap = Array.from({ length: 100 }, (_, i) => {
      const rand = Math.random();
      if (rand < 0.1) return 0;
      if (rand < 0.3) return 83 + Math.random() * 1;
      if (rand < 0.5) return 84 + Math.random() * 2;
      if (rand < 0.8) return 86 + Math.random() * 2;
      return 88 + Math.random() * 2;
    });
    
    const values = heatmap.filter(v => v > 0);
    return {
      data: {
        heatmap,
        average: values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 79.2,
        best: Math.max(...heatmap),
        worst: Math.min(...heatmap)
      }
    };
  }
}

const mockApiService = new MockApiService();
export default mockApiService;

